import {
  FormFieldWidget,
  FormM2MTableFieldWidget,
  ModelFieldType,
  SPI,
  ViewType,
  Widget,
  ValidatorInfo,
  ValidatorStatus,
  isValidatorSuccess,
  ActionType,
  ActionWidgetProps,
  ServerActionWidget,
  ActionWidget,
  RuntimeServerAction,
  RuntimeFunctionDefinition,
  ActiveRecords,
  FunctionService,
  RequestModelField,
  StaticMetadata,
  SubmitValue,
  ActiveRecord,
  ClickResult
} from '@kunlun/dependencies';
import { cloneDeep, omit, pick } from 'lodash-es';
import XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { OioMessage, OioNotification } from '@kunlun/vue-ui-antd';
import { Modal } from 'ant-design-vue';
import { createVNode, nextTick } from 'vue';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import TradeOrderItemDetail from './TradeOrderItemDetail.vue';
import { iterateObjDeep } from '../../../../utils/object';
import { removeDecimalZero } from '../../../../utils/number';
import { orderPreviewAudit, orderPreviewCreate, quicklyAdd } from './services';
import { generateRandomNumber } from '../MindMap/utils';
import { calcKarNum } from '../../../../utils/productPackage';
import { queryPromotionType } from '../MindMap/services';

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToMany,
    widget: 'Table',
    name: 'createOrderSaleSkuList',
    model: [
      'deli.aries.seller.SellerDistributionCreateOrderParam',
      'deli.aries.seller.SellerDistributorAuditOrderParam'
    ],
    multi: true
  })
)
export class FormM2mMultiTradeOrderItemDetailFieldWidget extends FormM2MTableFieldWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(TradeOrderItemDetail);
    return this;
  }

  @Widget.Reactive()
  private mountedProcessFlag = false;

  @Widget.Reactive()
  get rModel() {
    return this.model;
  }

  @Widget.Reactive()
  private get disabledImport() {
    return this.model.model === 'deli.aries.seller.SellerDistributorAuditOrderParam' && ['LIVE_CREATE'].includes(this.formData.source as string);
  }

  // 审核订单是否禁止增减明细行
  @Widget.Reactive()
  get disabledAddOrDelRow() {
    // 未排产：NO_PRODUCING
    // 已排产：IN_PRODUCING
    return (
      this.model.model === 'deli.aries.seller.SellerDistributorAuditOrderParam' &&
      (
        (['CUSTOM_MADE'].includes(this.formData.orderType as string) && this.formData.rejectionEnum === 'IN_PRODUCING')
        || ['LIVE_CREATE'].includes(this.formData.source as string)
      )
    );
  }

  @Widget.Reactive()
  private get disabledFastAddRow() {
    return this.model.model === 'deli.aries.seller.SellerDistributorAuditOrderParam' && ['LIVE_CREATE'].includes(this.formData.source as string);
  }

  @Widget.Reactive()
  public baseData: {
    disabled: boolean;
    editBizTypeArr: string[];
    editOrderTypeArr: string[];
    promotionTypeEnumList: null | { name: string; value: string }[];
  } = {
      promotionTypeEnumList: null,
      editBizTypeArr: ['CHANGE_ORDER'],
      editOrderTypeArr: ['GIFT'],
      disabled: true
    };
  @Widget.Watch('formData', { deep: true, immediate: true })
  public watchFormData(val) {
    if (this.baseData.editBizTypeArr.includes(this.formData?.bizType as any ?? '') || this.baseData.editOrderTypeArr.includes(this.formData?.orderType as any ?? '')) {
      this.baseData.disabled = false;
    } else {
      this.baseData.disabled = true;
    }
  }

  @Widget.Reactive()
  private state: {
    // 显示赠品弹窗
    showGiftDrawer: boolean;
    // 赠品弹窗中选中的活动
    selectedActivePromotionId?: string;
    // 商品排列类型
    cartGroupType: 'PROMOTION' | 'ADD_CART_SEQ';
  } = {
      showGiftDrawer: false,
      cartGroupType: 'ADD_CART_SEQ'
    };

  @Widget.Method()
  private setState(val: Partial<typeof this.state>) {
    Object.assign(this.state, val);
  }

  @Widget.Method()
  private assignToFormData(obj) {
    Object.assign(this.formData, obj);
  }

  @Widget.Method()
  async quicklyAdd(itemDetailStr: string) {
    if (!this.formData.buyer || !this.formData.salesOffice) {
      OioMessage.error('请将买卖家信息填写完整!');
      return;
    }
    if (!this.rModel) {
      throw new Error('data error');
    }

    const data: Record<any, any> = cloneDeep({
      ...pick(this.formData, [
        'buyerPartnerId',
        'salesOffice',
        'buyer',
        'sellerPartnerRelationApplySearch',
        'orderType',
        'bizType'
      ]),
      itemDetailStr
    });

    // 集中收单
    if (this.formData.orderType === 'CENTRALIZED_ACQUIRING'||this.formData.bizType === 'POLICY_PROMOTION') {
      data.deliAriesPromotionPlan = cloneDeep(this.formData.deliAriesPromotionPlan);
      delete data.deliAriesPromotionPlan?.META_BIT;
      delete data.deliAriesPromotionPlan?._d_model;
    }

    iterateObjDeep(data, (obj: Record<any, any>) => {
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    const res = await quicklyAdd(this.rModel, { data }, this.isPromotionOrder);

    calcKarNum(res.createOrderSaleSkuList, 'deliAriesItemSpecificationsProxyList', 'itemNum', 'itemKarNum');
    res.createOrderSaleSkuList?.forEach((i) => (i.uuid = generateRandomNumber()));

    return res;
  }

  @Widget.Reactive()
  private get isPromotionOrder() {
    return ['PROMOTION', 'CENTRALIZED_ACQUIRING', 'EXHIBITION', 'DROPSHIPPING'].includes(
      this.formData.orderType as string
    );
  }

  private filterGift(list: any[]) {
    return list.filter((item) => !(this.isPromotionOrder && item.isGifted)) ?? [];
  }

  @Widget.Method()
  private resetData() {
    Object.assign(this.formData, {
      createOrderSaleSkuList: [],
      activityDiscountAmount: '0',
      actualAmount: '0',
      actualShippingFee: '0',
      expectedAmount: '0',
      manualDiscountAmount: '0',
      originAmount: '0',
      platformDiscountAmount: '0',
      settlementAmount: '0',
      shopDiscountAmount: '0',
      discountAmount: '0',
      itemNumTotal: null,
      volumeTotal: null,
      heightTotal: null,
      selectGifts: null,
      cartItemForPromotions: null
    });
    this.setState({ selectedActivePromotionId: undefined });
  }

  @Widget.Method()
  async orderPreview(dataSource: any[]) {
    const fnMap = {
      'deli.aries.seller.SellerDistributionCreateOrderParam': orderPreviewCreate,
      'deli.aries.seller.SellerDistributorAuditOrderParam': orderPreviewAudit
    } as const;
    const fn = fnMap[this.field.model];

    if (!fn || !this.rModel) {
      throw new Error('data error');
    }

    const uuidMap = new Map(dataSource.map((item) => [item.outCode, item.uuid]));
    const formData: Record<any, any> = cloneDeep(this.formData);
    delete formData.editTableStatus;

    formData.createOrderSaleSkuList = cloneDeep(dataSource);
    formData.createOrderSaleSkuList =
      formData.createOrderSaleSkuList?.filter((item) => {
        delete item.itemKarNum;
        delete item.uuid;
        // delete item.promotionSimpleCodeObjId;
        delete item.promotionSimpleCodeObj;
        // item.tradeOrderDetailId = null
        return item.outCode && !(this.isPromotionOrder && item.isGifted);
        // item.itemNum &&
        // !(this.formData.orderType === 'PROMOTION' && this.formData.bizType === 'POLICY_PROMOTION' && item.isGifted)
      }) ?? [];
    // 所有商品都无数量或不存在商品，清空赠品相关数据
    if (!formData.createOrderSaleSkuList.length) {
      const skuList = this.filterGift(this.formData.createOrderSaleSkuList as any[]);
      this.resetData();
      this.formData.createOrderSaleSkuList = skuList;

      return {
        createOrderSaleSkuList: []
      };
    }

    iterateObjDeep(formData, (obj: Record<any, any>) => {
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    if (formData.deliAriesPromotionPlan) {
      delete formData.deliAriesPromotionPlan.META_BIT;
      delete formData.deliAriesPromotionPlan._d_model;
    }
    //删除下拉菜单选项
    delete formData.ariesIssuingMethodOptions;
    delete formData.deliveryMethods;
    
    const resData = (await fn(this.rModel, { formData }, this.isPromotionOrder)) ?? {};
    // 表格数据由组件内部修改
    Object.assign(this.formData, omit(resData, ['createOrderSaleSkuList']));

    calcKarNum(resData.createOrderSaleSkuList, 'deliAriesItemSpecificationsProxyList', 'itemNum', 'itemKarNum');
    resData.createOrderSaleSkuList?.forEach((i) => (i.uuid = uuidMap[i.outCode] ?? generateRandomNumber()));
    return resData;
  }

  @Widget.Method()
  public exportFile(dataSource: any[]) {
    const priceTypeEnumList = {
      EX_FACTORY_PRICE: '出厂价',
      PRICE: '一口价',
      REAL_PRICE: '实价',
      RETAIL_PRICE: '零售价'
    };
    const InventoryStatus = {
      ALL_SATISFIED: '全部满足',
      PARTIALLY_FULFIL: '部分满足',
      NOT_SATISFIED: '不满足'
    };
    const TimeRuleMap = {
      DELIVERY_TIME: '按发货时间记账',
      TARGET_TIME: '按指定时间记账'
    };
    const formatIsGifted = (val) => {
      if (val === undefined) {
        return val;
      }
      return val ? '是' : '否';
    };

    const content = dataSource.map((item) => {
      const row = {
        ERP商品编码: item.outCode,
        考核标签: item.isExamine ?? true ? '考核' : '不考核',
        商品名称: item.itemName,
        价格类型: priceTypeEnumList[item.priceType],
        出厂价: removeDecimalZero(item.marketUnitPrice),
        加价系数: removeDecimalZero(item.priceRatio),
        特殊扣率: removeDecimalZero(item.saleDiscount),
        客户扣率: removeDecimalZero(item.customerDiscount),
        专业扣率: removeDecimalZero(item.specialityDiscount),
        活动扣率: removeDecimalZero(item.promotionDeductionRate),
        结算扣率: removeDecimalZero(item.finalSettlementDeductionRate),
        结算单价: removeDecimalZero(item.finalSettlementPrice),
        [this.isPromotionOrder ? '赠品' : '免费']: formatIsGifted(item.isGifted),
        包装单位: item.deliAriesItemSpecificationsProxyList
          ?.map((c) => `${(c?.count ?? 0) + c.unitName}(${c.conversionRules})`)
          .join(','),
        下单数量: item.itemNum,
        库存: item.availableNum,
        库存状态: InventoryStatus[item.deliAriesInventoryStatusEnum],
        促销活动: (
          item.canDesignShopMatchRules
            ?.filter((item2) => item.calcPromotionIds?.includes?.(item2.promotionId))
            ?.map((item) => item.ruleName) ?? []
        ).join(','),
        结算金额: removeDecimalZero(item.actualAmount),
        记账时间规则: TimeRuleMap[item.accountTimeRule],
        指定账期天数: item.paymentDays,
        指定记账时间: item.accountDate
      };
      if (!this.isPromotionOrder) {
        delete row['活动扣率'];
        delete row['促销活动'];
      }
      return row;
    });

    this.saveJsonToExcel(content, `代客下单商品明细${Date.now()}.xlsx`);
  }

  public saveJsonToExcel(data, filename) {
    const sheet = XLSX.utils.json_to_sheet(data);
    const workbook = {
      SheetNames: ['sheet1'],
      Sheets: {
        sheet1: sheet
      }
    };
    const wbout = XLSX.write(workbook, {
      bookType: 'xlsx',
      bookSST: true,
      type: 'array'
    });
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
  }

  protected async mounted() {
    super.mounted();
    const res = await queryPromotionType(this.model);
    this.baseData.promotionTypeEnumList = res?.promotionTypeEnumList?.map((item) => {
      return { label: item.displayName, value: item.value };
    });

    if (this.mountedCallChaining) {
      this.mountedCallChaining.hook(this.path, async () => {
        await this.mountedProcess();
      });
    } else {
      this.mountedProcess();
    }
  }

  protected async mountedProcess() {
    this.mountedProcessFlag = true;
    console.log(1);

    calcKarNum(
      (this.formData as any).createOrderSaleSkuList,
      'deliAriesItemSpecificationsProxyList',
      'itemNum',
      'itemKarNum'
    );
    (this.formData as any)?.createOrderSaleSkuList?.forEach((i) => (i.uuid = generateRandomNumber()));
  }

  @Widget.Reactive()
  setTableEditStatus(status: 'OPEN' | 'CLOSED') {
    this.formData.editTableStatus = status;
  }

  public async validator(): Promise<ValidatorInfo> {
    const res = await this.validatorSpecific(this.value);

    if (!isValidatorSuccess(res)) {
      return res;
    }

    // 自定义校验
    const errorPromotionId = (this.formData as Record<any, any>).selectGifts?.find((item) => {
      return item.selectGifts.reduce((acc, cur) => acc + Number(cur.itemNum ?? 0), 0) < item?.limitSelectNum ?? 0;
    })?.promotionId;

    if (errorPromotionId) {
      if (this.formData.orderType === 'EXHIBITION') {
        const unSelectNum = (this.formData as Record<any, any>).selectGifts?.reduce((acc, item) => {
          return (
            acc + (item?.limitSelectNum ?? 0) - item.selectGifts.reduce((acc2, cur) => acc2 + Number(cur.itemNum), 0)
          );
        }, 0);
        try {
          await new Promise<void>((resolve, reject) => {
            Modal.confirm({
              title: `当前订单，存在${unSelectNum}个赠品未选，是否继续提交？`,
              icon: createVNode(ExclamationCircleOutlined),
              onOk() {
                resolve();
              },
              onCancel() {
                reject();
              }
            });
          });
        } catch (e) {
          return {
            message: '还有未选择的赠品',
            status: ValidatorStatus.Error,
            path: ''
          };
        }
      } else {
        this.setState({
          showGiftDrawer: true,
          selectedActivePromotionId: errorPromotionId
        });

        return {
          message: '还有未选择的赠品',
          status: ValidatorStatus.Error,
          path: ''
        };
      }
    }


    this.formData.createOrderSaleSkuList =
      (this.formData.createOrderSaleSkuList as any[])?.filter((item) => item.outCode) ?? [];
    (this.formData.createOrderSaleSkuList as any[]).forEach((item) => {
      if (item.isExamine === undefined) {
        item.isExamine = true;
      }
      delete item.itemKarNum;
      // delete item.uuid;
    });

    return {
      message: '',
      status: ValidatorStatus.Success,
      path: ''
    };
  }
}
// 记账时间规则: TimeRuleMap[item.accountTimeRule],
// 指定账期天数: item.paymentDays,
// 指定记账时间: item.accountDate
// DELIVERY_TIME: '按发货时间记账',
// TARGET_TIME: '按指定时间记账'
const testSubmitValue = (data: any): boolean => {
  if (!data?.length) return false;
  let canPass = true;
  data.forEach((item: any) => {
    if (item.accountTimeRule === 'DELIVERY_TIME' && !item.paymentDays && item.paymentDays !== 0) {
      canPass = false;
    }
    if (item.accountTimeRule === 'TARGET_TIME' && !item.accountDate) {
      canPass = false;
    }
  });
  return canPass;
};
// 代客下单-保存按钮
@SPI.ClassFactory(
  ActionWidget.Token({
    actionType: ActionType.Server,
    name: ['submitCreateOrder'],
    model: 'deli.aries.seller.SellerDistributionCreateOrderParam',
    widget: 'ServerAction'
  })
)
// 代客下单-审核-保存/通过按钮
@SPI.ClassFactory(
  ActionWidget.Token({
    actionType: ActionType.Server,
    name: ['auditSave', 'submitAuditSuccess'],
    model: 'deli.aries.seller.SellerDistributorAuditOrderParam',
    widget: 'ServerAction'
  })
)
export class auditSave extends ServerActionWidget {
  protected executeFunction<T>(
    functionDefinition: RuntimeFunctionDefinition,
    requestFields: RequestModelField[],
    activeRecords: ActiveRecords | undefined
  ): Promise<T> {
    activeRecords = this.mergeContext(activeRecords);
    return FunctionService.INSTANCE.simpleExecute(
      this.model,
      functionDefinition,
      {
        requestModels: [StaticMetadata.ResourceAddress],
        requestFields,
        // 设置返回字段只有code，减小报文大小
        responseFields: [{ field: this.model.modelFields.find((i) => i.data === 'code')! }]
      },
      activeRecords
    );
  }

  protected async submit(action: RuntimeServerAction) {
    await new Promise((resolve, reject) => {
      // 等待表格编辑完成
      let interval = setInterval(() => {
        if (this.activeRecords![0].editTableStatus === 'CLOSED' || !this.activeRecords![0].editTableStatus) {
          clearInterval(interval);
          nextTick(() => {
            resolve(true);
          });
        }
      }, 200);
    });
    // 订单审核过滤数据
    if (
      this.model.model === 'deli.aries.seller.SellerDistributorAuditOrderParam' &&
      this.action.name === 'submitAuditSuccess'
    ) {
      this.activeRecords![0].createOrderSaleSkuList = (this.activeRecords![0].createOrderSaleSkuList as any[]).filter(
        (item: any) => !item.allowDelete || item.itemNum > 0
      );
    }
    return super.submit(action);
  }

  //代客下单页面商品校验
  private __executeSuccess = true;
  protected executeAction(action: RuntimeServerAction, submitValue: SubmitValue): Promise<ClickResult> {
    const formData = submitValue.records! as ActiveRecord;
    const createOrderSaleSkuList: any = formData.createOrderSaleSkuList;
    if (!createOrderSaleSkuList?.length) {
      OioNotification.error('错误', '请添加商品');
      this.__executeSuccess = false;
      return null as any;
    }
    if (testSubmitValue(createOrderSaleSkuList)) {
      this.__executeSuccess = true;
      return super.executeAction(action, submitValue);
    } else {
      OioNotification.error('错误', '请检查商品记账时间规则及配置项');
      this.__executeSuccess = false;
      return null as any;
    }
  }
  protected clickActionAfter(result: ClickResult): Promise<ClickResult> {
    if (!this.__executeSuccess) {
      return null as any;
    }
    return super.clickActionAfter(result);
  }
}
